<template>
  <SearchSidebarGroup :label="label" class="" :disabled="!inputValue">
    <div class="pb-15 pt-40">
      <Slider
        v-if="inputValue"
        id="slider-fit"
        v-model.lazy="inputValue"
        :classes="sliderClasses"
        :min="min"
        :max="max"
      />
      <div v-else>
        {{ $texts('search.rangeFilterNotAvailable', 'Filter not available') }}
      </div>
    </div>
  </SearchSidebarGroup>
</template>

<script lang="ts" setup>
import Slider from '@vueform/slider'

const props = defineProps<{
  filterKey: string
  label: string
  min: number | null
  max: number | null
}>()

const route = useRoute()
const router = useRouter()

const minKey = computed(() => props.filterKey + '_min')
const maxKey = computed(() => props.filterKey + '_max')

/**
 * Return a valid value for the given range key.
 */
function getValidRangeValue(
  key: string,
  defaultValue: number | null,
): number | null {
  // If any of these values is null it means that the filter is not available
  // for the current filter/facet combination.
  if (props.min === null || props.max === null || defaultValue === null) {
    return null
  }

  // Get the query value.
  const v = route.query[key]
  if (typeof v !== 'string') {
    return defaultValue
  }

  // Try to parse the query value as an integer.
  const numeric = parseInt(v)
  if (isNaN(numeric)) {
    return defaultValue
  }

  // Validate the number.
  if (numeric < props.min || numeric > props.max) {
    return defaultValue
  }

  return numeric
}

const inputValue = computed({
  get(): [number, number] | undefined {
    const min = getValidRangeValue(minKey.value, props.min)
    const max = getValidRangeValue(maxKey.value, props.max)

    // Return undefined if any of the values is not valid.
    if (!min || !max) {
      return undefined
    }

    // Return the valid range values.
    return [min, max]
  },
  set(newValue: [number, number] | undefined) {
    const min = newValue?.[0]
    const max = newValue?.[1]
    const newRoute = {
      path: route.path,
      query: {
        ...route.query,
        [minKey.value]: min && min !== props.min ? min : undefined,
        [maxKey.value]: max && max !== props.max ? max : undefined,
      },
    }
    if (route.query[minKey.value] || route.query[maxKey.value]) {
      // The user is already using the range filter currently:
      // Replace the current route with the updated values.
      router.replace(newRoute)
    } else {
      // The user has not yet used the range filter:
      // Push a new history entry for the filter.
      router.push(newRoute)
    }
  },
})

const sliderClasses = {
  target:
    'relative box-border select-none touch-none tap-highlight-transparent touch-callout-none disabled:cursor-not-allowed',
  focused: 'slider-focused',
  tooltipFocus: 'slider-tooltip-focus',
  tooltipDrag: 'slider-tooltip-drag',
  ltr: 'slider-ltr',
  rtl: 'slider-rtl',
  horizontal: 'slider-horizontal h-[6px]',
  vertical: 'slider-vertical w-[6px] h-[80px]',
  textDirectionRtl: 'slider-txt-rtl',
  textDirectionLtr: 'slider-txt-ltr',
  base: 'w-full h-full relative z-1 bg-gray rounded',
  connects: 'w-full h-full relative overflow-hidden z-0 rounded',
  connect:
    'absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full bg-black cursor-pointer tap:duration-300 tap:transition-transform disabled:bg-gray disabled:cursor-not-allowed',
  origin:
    'slider-origin absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full h:h-0 v:-top-full txt-rtl-h:left-0 txt-rtl-h:right-auto v:w-0 tap:duration-300 tap:transition-transform',
  handle:
    'absolute rounded-full bg-white border-0 shadow-slider cursor-grab focus:outline-none h:w-[16px] h:h-[16px] h:top-[-6px] h:right-[-8px] txt-rtl-h:left-[-8px] txt-rtl-h:right-auto v:w-[16px] v:h-[16px] v:top-[-8px] v:right-[-5px] disabled:cursor-not-allowed focus:ring focus:ring-green-500 focus:ring-opacity-30',
  handleLower: 'slider-hande-lower',
  handleUpper: 'slider-hande-upper',
  touchArea: 'h-full w-full',
  tooltip:
    'absolute block text-sm font-semibold whitespace-nowrap py-[4px] px-[6px] min-w-[20px] text-center text-white rounded border border-green-500 bg-black transform h:-translate-x-1/2 h:left-1/2 v:-translate-y-1/2 v:top-1/2 disabled:bg-gray merge-h:translate-x-1/2 merge-h:left-auto merge-v:-translate-x-[16px] merge-v:top-auto tt-focus:hidden tt-focused:block tt-drag:hidden tt-dragging:block',
  tooltipTop: 'bottom-[24px] h:arrow-bottom merge-h:bottom-[14px]',
  tooltipBottom: 'top-[24px] h:arrow-top merge-h:top-[20px]',
  tooltipLeft: 'right-[24px] v:arrow-right merge-v:right-[4px]',
  tooltipRight: 'left-[24px] v:arrow-left merge-v:left-[28px]',
  tooltipHidden: 'slider-tooltip-hidden',
  active: 'slider-active shadow-slider-active cursor-grabbing',
  draggable: 'cursor-ew-resize v:cursor-ns-resize',
  tap: 'slider-state-tap',
  drag: 'slider-state-drag',
}
</script>
